import React from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink } from "lucide-react";
import SiteNavbar from "@/components/SiteNavbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { CASE_IMAGES, CASE_LINKS } from "@/data/site";

const STUDY_ONE_STATS = [
  { value: "49,000", label: "Reached" },
  { value: "4,600", label: "Link Clicks" },
  { value: "#1", label: "on Google" },
];

export default function CaseStudies() {
  return (
    <div className="min-h-screen bg-[#0a1f3d] text-white">
      <SiteNavbar />

      <main className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-white/80">
          <Star className="h-4 w-4 text-[#e9a31e]" />
          Case Studies
        </div>

        <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-6xl">
          Real <span className="text-[#e9a31e]">Results</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-white/70">
          Real businesses. Real results. No made-up numbers.
        </p>

        {/* Case Study 1 — SOHO.69 */}
        <Reveal>
        <section className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#e9a31e]">
                Google SEO and Meta Ads Management
              </span>
              <h2 className="mt-3 text-3xl font-extrabold sm:text-4xl">
                SOHO.69 Tenerife
              </h2>
              <p className="mt-4 text-white/75">
                We took SOHO.69 from invisible online to the #1 result on Google
                and Google Maps in Tenerife, while reaching 49,000 potential
                customers through targeted Meta ad campaigns.
              </p>

              <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-[#e9a31e]">
                In the first month:
              </p>
              <div className="mt-3 grid gap-4 sm:grid-cols-3">
                {STUDY_ONE_STATS.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-xl border border-white/10 bg-[#081831] p-5 text-center"
                  >
                    <p className="text-3xl font-extrabold text-[#e9a31e]">
                      {s.value}
                    </p>
                    <p className="mt-2 text-sm text-white/60">{s.label}</p>
                  </div>
                ))}
              </div>

              <a
                href={CASE_LINKS.soho}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-[#e9a31e] px-6 py-3.5 text-base font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743]"
              >
                Visit Website <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            <img
              src={CASE_IMAGES.soho}
              alt="SOHO.69 Tenerife results"
              className="w-full rounded-xl border border-white/10 object-cover md:mx-auto md:max-w-sm"
            />
          </div>
        </section>
        </Reveal>

        {/* Case Study 2 — Mobile Kitchen Company */}
        <Reveal>
        <section className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <img
              src={CASE_IMAGES.mkc}
              alt="Mobile Kitchen Company website"
              className="w-full rounded-xl border border-white/10 object-cover"
            />
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#e9a31e]">
                Web Design
              </span>
              <h2 className="mt-3 text-3xl font-extrabold sm:text-4xl">
                Mobile Kitchen Company
              </h2>

              <dl className="mt-5 space-y-3 text-white/80">
                <div className="flex gap-2">
                  <dt className="font-semibold text-white">Industry:</dt>
                  <dd>Mobile Kitchen Hire, Surrey</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-semibold text-white">Service:</dt>
                  <dd>Web Design</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-semibold text-white">Result:</dt>
                  <dd>
                    Full website built from scratch. Now ranking in Surrey
                    search results for mobile kitchen hire.
                  </dd>
                </div>
              </dl>

              <a
                href={CASE_LINKS.mkc}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-[#e9a31e] px-6 py-3.5 text-base font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743]"
              >
                Visit Website <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
        </Reveal>

        {/* CTA */}
        <Reveal>
        <div className="mt-14 flex flex-col items-start gap-5 rounded-2xl border border-white/10 bg-gradient-to-br from-[#0e2647] to-[#0a1f3d] p-8 md:flex-row md:items-center md:justify-between">
          <h3 className="text-2xl font-bold">Your business could be next</h3>
          <Link
            to="/audit"
            className="w-full shrink-0 rounded-lg bg-[#e9a31e] px-6 py-3.5 text-center text-base font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743] md:w-auto"
          >
            Book Your Free Audit
          </Link>
        </div>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
